import React, { useState } from 'react';
import { auth } from '../services/firebaseConfig';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useNavigate, Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { logGoogleSignInAttempt, logGoogleSignInSuccess, logGoogleSignInError } from '../services/analyticsLogger';
import { trackLead } from '../services/fbPixel';
import '../styles/LoginPage.css';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const googleProvider = new GoogleAuthProvider();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const token = await user.getIdToken();
      localStorage.setItem('token', token);
      trackLead({ method: 'email' });
      navigate('/sessions');
    } catch (error) {
      console.error(error);
      setError('Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      console.log('🔐 Starting Google sign-in...');
      logGoogleSignInAttempt('login');
      
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      console.log('✅ Google sign-in successful:', {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName
      });
      
      logGoogleSignInSuccess(user.uid, user.email, 'login');
      
      const token = await user.getIdToken();
      localStorage.setItem('token', token);
      trackLead({ method: 'google' });
      console.log('✅ Token saved, navigating to sessions...');
      navigate('/sessions');
    } catch (error) {
      console.error('❌ Google sign-in error:', {
        code: error.code,
        message: error.message,
        fullError: error
      });
      
      logGoogleSignInError(error.code, error.message, 'login');
      
      // Handle specific error cases
      if (error.code === 'auth/popup-closed-by-user') {
        console.log('ℹ️ User closed the popup');
        // Don't show error, but ensure loading state is cleared
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup was blocked by your browser. Please allow popups for this site.');
      } else if (error.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized for Google Sign-In. Please contact support.');
        console.error('⚠️ CRITICAL: Domain not authorized in Firebase Console');
      } else if (error.code === 'auth/operation-not-allowed') {
        setError('Google Sign-In is not enabled. Please contact support.');
        console.error('⚠️ CRITICAL: Google provider not enabled in Firebase Console');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        console.log('ℹ️ Multiple popup requests - user likely clicked button multiple times');
      } else {
        setError(`Failed to sign in with Google: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="login-container">
        <div className="login-content">
          <div className="login-header">
            <h1>Welcome Back!</h1>
            <p>Sign in to continue your karaoke journey</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <div className="input-wrapper">
                <i className="fas fa-envelope"></i>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <i className="fas fa-lock"></i>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            <button 
              type="submit" 
              className={`login-button ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="loading-spinner"></span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="divider">
            <span>or</span>
          </div>

          <button 
            onClick={handleGoogleLogin}
            className="google-login-button"
            disabled={isLoading}
          >
            <i className="fab fa-google"></i>
            Continue with Google
          </button>

          <div className="login-footer">
            <p>Don't have an account? <Link to="/register">Sign up</Link></p>
            <Link to="/forgot-password" className="forgot-password">Forgot Password?</Link>
          </div>
        </div>

        <div className="login-background">
          <div className="background-overlay"></div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default LoginPage;
